// Textos de posicionamiento (SEO) de cada página pública.
// Se usan en dos sitios: al compilar (vite.config.ts genera un HTML propio por página, con su título,
// descripción, canonical y contenido legible sin JavaScript) y en la web (título al navegar y guía bajo el juego).
// No importar nada del navegador aquí: este archivo también lo lee Node al compilar.

export const SITE_URL = 'https://jugaly.es';

export interface GuideSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface SeoPage {
  path: string;
  title: string;          // <title>: lo que sale en azul en Google (≈ 60 caracteres)
  description: string;    // lo que sale debajo en Google (≈ 155 caracteres)
  h1: string;
  intro: string;
  sections: GuideSection[];
  game?: { name: string; genre: string; keywords: string };
}

export const SEO_PAGES: SeoPage[] = [
  {
    path: '/',
    title: 'Jugaly · Juegos online gratis con ranking: Buscaminas y Snake',
    description: 'Juega gratis al Buscaminas y al Snake en el navegador, sin descargas. El mismo nivel para todos y una clasificación por juego para competir.',
    h1: 'Juegos online gratis con ranking',
    intro: 'Jugaly reúne juegos clásicos y exigentes para jugar al momento en el navegador, en el ordenador o en el móvil. Cada juego tiene un único nivel igual para todos y su propia clasificación.',
    sections: [
      { heading: 'Juegos disponibles', list: ['Buscaminas online: tablero de 16 × 16 con 40 minas y ranking por mejor tiempo.', 'Snake online: el juego de la serpiente, cada vez más rápido, con ranking por puntuación.'] },
    ],
  },
  {
    path: '/buscaminas',
    title: 'Buscaminas online gratis con ranking · Jugaly',
    description: 'Juega al Buscaminas online gratis, sin descargas. Tablero de 16 × 16 con 40 minas igual para todos y clasificación por mejor tiempo. En el móvil y el ordenador.',
    h1: 'Buscaminas online gratis',
    intro: 'El Buscaminas de siempre, en el navegador y con clasificación. Todo el mundo juega el mismo nivel, un tablero de 16 × 16 con 40 minas, así que tu mejor tiempo se compara en igualdad con el resto de jugadores.',
    game: { name: 'Buscaminas', genre: 'Puzle', keywords: 'buscaminas, buscaminas online, buscaminas gratis, buscaminas con ranking, minesweeper' },
    sections: [
      {
        heading: 'Cómo jugar al Buscaminas',
        list: [
          'Toca cualquier casilla para empezar: el primer toque nunca es una mina y abre una zona del tablero.',
          'Cada número indica cuántas minas hay en las ocho casillas que lo rodean.',
          'Marca con una bandera las casillas donde sepas que hay una mina (clic derecho, o mantener pulsado en el móvil).',
          'Toca un número que ya tenga todas sus banderas para abrir de golpe las casillas de alrededor.',
          'Ganas al descubrir todas las casillas sin mina. Si pisas una, se acaba la partida.',
        ],
      },
      {
        heading: 'Clasificación del Buscaminas',
        paragraphs: [
          'El ranking guarda el mejor tiempo de cada jugador registrado. El reloj empieza con tu primer toque y se mide en centésimas de segundo.',
          'Cada partida se comprueba en el servidor movimiento a movimiento, así que los tiempos de la clasificación son reales.',
        ],
      },
      {
        heading: 'Consejos para bajar tu tiempo',
        list: [
          'Busca primero los patrones seguros, como el 1-2-1 y el 1-2-2-1 junto a una pared.',
          'Usa «abrir alrededor» en vez de descubrir casilla a casilla.',
          'No pongas banderas que no necesites: cada clic cuenta.',
        ],
      },
      {
        heading: 'Preguntas frecuentes',
        list: [
          '¿Es gratis? Sí, el Buscaminas de Jugaly es gratis y no hace falta descargar nada.',
          '¿Funciona en el móvil? Sí, el tablero se ajusta a la pantalla y se juega con toques.',
          '¿Necesito cuenta? Para jugar no. Para guardar tus tiempos y salir en la clasificación, sí.',
        ],
      },
    ],
  },
  {
    path: '/snake',
    title: 'Snake online gratis con clasificación · Jugaly',
    description: 'Juega al Snake, el juego de la serpiente, online y gratis. Cada manzana la hace más rápida. Mismo tablero para todos y clasificación por puntuación.',
    h1: 'Snake online gratis',
    intro: 'El clásico juego de la serpiente en el navegador, con movimiento fluido y una clasificación por puntuación. Todo el mundo juega el mismo tablero de 20 × 20, y la serpiente acelera con cada punto.',
    game: { name: 'Snake', genre: 'Arcade', keywords: 'snake, snake online, juego de la serpiente, snake gratis, snake con clasificación' },
    sections: [
      {
        heading: 'Cómo jugar al Snake',
        list: [
          'Pulsa una flecha (o WASD) para empezar. En el móvil, desliza el dedo sobre el tablero.',
          'Cada manzana suma un punto y alarga la serpiente.',
          'La serpiente va más rápida con cada punto: empieza tranquila y termina a toda velocidad.',
          'Si chocas contra la pared o contra tu propia cola, se acaba la partida.',
          'Espacio para pausar. Si cambias de pestaña, el juego se pausa solo.',
        ],
      },
      {
        heading: 'Clasificación del Snake',
        paragraphs: [
          'El ranking guarda la mejor puntuación de cada jugador registrado.',
          'El servidor repite cada partida a partir de tus giros para calcular la puntuación, así que nadie puede inventarse los puntos.',
        ],
      },
      {
        heading: 'Consejos para hacer más puntos',
        list: [
          'Muévete pegado a las paredes y deja el centro libre para maniobrar.',
          'Cuando la serpiente sea larga, recorre el tablero en zigzag para no encerrarte.',
          'Piensa el siguiente giro antes de llegar a la manzana: a alta velocidad no hay tiempo para corregir.',
        ],
      },
      {
        heading: 'Preguntas frecuentes',
        list: [
          '¿Es gratis? Sí, el Snake de Jugaly es gratis y no hace falta descargar nada.',
          '¿Funciona en el móvil? Sí, se juega deslizando el dedo y el tablero se ajusta a la pantalla.',
          '¿Necesito cuenta? Para jugar no. Para guardar tu puntuación y salir en la clasificación, sí.',
        ],
      },
    ],
  },
  {
    path: '/ranking',
    title: 'Ranking de Buscaminas y Snake · Clasificación · Jugaly',
    description: 'Consulta la clasificación de Jugaly: los mejores tiempos del Buscaminas y las mejores puntuaciones del Snake. Juega gratis y entra en el ranking.',
    h1: 'Ranking',
    intro: 'Las clasificaciones de Jugaly: el mejor tiempo de cada jugador en el Buscaminas y la mejor puntuación en el Snake.',
    sections: [],
  },
];

export const SEO_BY_PATH: Record<string, SeoPage> = Object.fromEntries(SEO_PAGES.map((p) => [p.path, p]));
